import React, { useState } from 'react';
import { useRouter } from 'next/router';
import WidthWrapperEx from '../layout/WidthWrapperEx';
import SectionBar from '../ui/SectionBar';
import Button from '../ui/Button';
import AuctionCard from './AuctionCard';
import AuctionFAQ from './AuctionFAQ';
import { useTranslation } from '../../hooks/useTranslation';

export default function AuctionList({ auctions = [], loadError = false }) {
  const { t } = useTranslation('auctions');
  const router = useRouter();
  const [filter, setFilter] = useState('all');
  const phases = ['all', 'live', 'scheduled', 'ended'];
  const label = phase => t(phase === 'all' ? 'list.all' : phase === 'ended' ? 'list.recent' : 'phase.' + phase);
  const count = phase => phase === 'all' ? auctions.length : auctions.filter(auction => auction.phase === phase).length;
  const visible = auctions.filter(auction => filter === 'all' || auction.phase === filter);
  return <WidthWrapperEx size="detail" fullHeight={false} className="px-4 py-4 text-krit-surface sm:px-6 sm:py-10">
    <header className="mb-4 sm:mb-6">
      <h1 className="text-[30px] font-black tracking-tight sm:text-[40px]">{t('list.title')}<span className="text-teal-600">.</span></h1>
      <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-krit-muted sm:block">{t('list.description')}</p>
      <a href="#auction-faq" className="mt-2 inline-flex min-h-[44px] items-center text-xs font-bold underline underline-offset-4 hover:text-teal-700">{t('faq.link')}</a>
    </header>
    {!loadError && auctions.length > 0 && <SectionBar className="mb-4 sm:mb-6">
      <select aria-label={t('list.filter')} value={filter} onChange={event => setFilter(event.target.value)}
        className="min-h-[44px] w-full rounded-lg border-0 bg-white px-3 text-base font-bold text-krit-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 sm:!hidden">
        {phases.map(phase => <option key={phase} value={phase}>{label(phase)} ({count(phase)})</option>)}
      </select>
      <div role="group" aria-label={t('list.filter')} className="hidden flex-wrap gap-2 sm:flex">
        {phases.map(phase => {
          return <button key={phase} type="button" aria-pressed={filter === phase} onClick={() => setFilter(phase)}
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 ${filter === phase ? 'bg-krit-surface text-white' : 'bg-white/80 hover:bg-white'}`}>
            {label(phase)}
            <span className={`rounded-md px-1.5 py-0.5 text-[10px] ${filter === phase ? 'bg-white/15' : 'bg-krit-paper'}`}>{count(phase)}</span>
          </button>;
        })}
      </div>
    </SectionBar>}
    {loadError ? <div role="alert" className="rounded-xl bg-krit-paper p-6"><p className="mb-4 text-sm">{t('list.error')}</p><Button onClick={() => router.reload()} variant="secondary">{t('list.retry')}</Button></div>
      : visible.length ? <div data-auction-grid className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 sm:gap-x-5 lg:grid-cols-4">
        {visible.map((auction, index) => <AuctionCard key={auction.id} auction={auction} priority={index < 4} />)}
      </div> : <p role="status" className="rounded-xl bg-krit-paper px-5 py-10 text-sm text-krit-muted">{t(auctions.length ? 'list.emptyFilter' : 'list.empty')}</p>}
    <AuctionFAQ />
  </WidthWrapperEx>;
}
